import { FotoDoProduto } from './FotoDoProduto';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import { primeiroNome } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  ROTULO_DA_ETAPA,
  ROTULO_DA_ETIQUETA,
  ROTULO_DO_ESTADO,
  ROTULO_DO_RESULTADO,
  ha,
  hora,
  iniciarAnalise,
  lerFila,
  numeroDoCaso,
  urlDoRelatorio,
} from '../../auditoria';
import type {
  AbaDaFila,
  CasoDaFila,
  EstadoDoCaso,
  EtiquetaDoCaso,
  FilaDoAuditor,
  FiltrosDaFila,
  ResultadoDaAuditoria,
} from '../../auditoria';
import { ErroDeArea, EstadoVazio, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import './central.css';
import {
  ETAPAS_DA_FILA,
  ETAPA_INICIAL,
  FAIXAS,
  ORDENS,
  PERIODOS,
  VAZIO,
  consultaDe,
  deParametros,
  diaBR,
  enderecoDaCentral,
  enderecoDoCaso,
  guardarRolagem,
  hojeBR,
  intervalo,
  retomarRolagem,
} from './contexto-da-fila';
import type { FaixaDaDiferenca, Filtros, Periodo } from './contexto-da-fila';
import type { EtapaDaDivergencia, OrdemDaFila } from '../../auditoria';
import { CartoesDeEtapa, Paginacao, TabelaDeEtapa } from './FilasPorEtapa';

/**
 * TELA 22 — CENTRAL DE DIVERGÊNCIAS. "Divergência é pergunta, não acusação."
 *
 *   A  1366: cinco indicadores, filtros numa linha e a fila densa (tabela)
 *   B  390:  a mesma fila em cartões; filtros avançados em bottom sheet
 *   C  gestão (giro.administrar / auditoria.ver_todos): relatório CSV e análise por produto
 *
 * Status e resultado são DOIS selos: um caso pode estar concluído com "problema de dados".
 * O período (Hoje / 7 / 30 dias) é a janela dos CONCLUÍDOS — caso aberto aparece sempre.
 * Tudo o que aparece aqui vem de /api/auditoria/fila: sem dado, estado vazio de verdade.
 */

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'pronto'; readonly dados: FilaDoAuditor }
  | { readonly fase: 'erro'; readonly falha: Falha; readonly ultimo: FilaDoAuditor | null };

const ABAS: readonly { aba: AbaDaFila; rota: string; rotulo: string }[] = [
  { aba: 'fila', rota: 'divergencias', rotulo: 'Fila' },
  { aba: 'recontagens', rota: 'recontagens', rotulo: 'Recontagens' },
  { aba: 'concluidas', rota: 'concluidas', rotulo: 'Concluídas' },
  { aba: 'historico', rota: 'historico-auditoria', rotulo: 'Histórico' },
];

const TITULO_DA_ABA: Readonly<Record<AbaDaFila, string>> = {
  fila: 'O que precisa da sua análise',
  recontagens: 'Recontagens a fazer na loja',
  concluidas: 'Casos concluídos',
  historico: 'Histórico de divergências',
};

/** Casos de antes das três etapas (dono, 08/10): fecham pela recontagem de antes. */
const SUBTITULO_DA_ETAPA: Readonly<Record<EtapaDaDivergencia, string>> = {
  PRATELEIRA: 'Contar quanto tem em cima',
  ESTOQUE: 'Contar tudo o que tem embaixo',
  AJUSTE: 'Foi erro do balconista ou não',
  CONCLUIDA: 'Caso concluído',
};

const PRIORIDADE: Readonly<Record<FilaDoAuditor['prioridadePor'], string>> = {
  DIFERENCA: 'maior diferença primeiro',
  IDADE: 'mais antigo primeiro',
  VALOR: 'maior diferença e maior quantidade primeiro',
};

/** Os chips de status da fila: um status por vez (o status particiona a fila). */
const CHIPS_DE_STATUS: readonly {
  estado: EstadoDoCaso;
  rotulo: string;
  curto: string;
  tom: string;
  conta: (i: FilaDoAuditor['indicadores']) => number;
}[] = [
  { estado: 'NOVA', rotulo: 'Novas', curto: 'Novas', tom: 'nova', conta: (i) => i.novas },
  {
    estado: 'REABERTA',
    rotulo: 'Reabertas',
    curto: 'Reabertas',
    tom: 'nova',
    conta: (i) => i.reabertas,
  },
  {
    estado: 'EM_ANALISE',
    rotulo: 'Em análise',
    curto: 'Em análise',
    tom: '',
    conta: (i) => i.emAnalise,
  },
  {
    estado: 'AGUARDANDO_DADOS',
    rotulo: 'Aguardando dados',
    curto: 'Dados',
    tom: 'dados',
    conta: (i) => i.aguardandoDados,
  },
  {
    estado: 'RECONTAGEM',
    rotulo: 'Recontagem',
    curto: 'Recontagem',
    tom: '',
    conta: (i) => i.recontagens,
  },
];

const ESTADOS_DO_FILTRO: readonly { estado: EstadoDoCaso; rotulo: string }[] = [
  { estado: 'NOVA', rotulo: 'Novas' },
  { estado: 'EM_ANALISE', rotulo: 'Em análise' },
  { estado: 'AGUARDANDO_DADOS', rotulo: 'Aguardando dados' },
  { estado: 'RECONTAGEM', rotulo: 'Recontagem' },
  { estado: 'CONCLUIDA', rotulo: 'Concluídas' },
  { estado: 'REABERTA', rotulo: 'Reabertas' },
];
const RESULTADOS: readonly ResultadoDaAuditoria[] = [
  'BALCONISTA_CORRETO',
  'ERRO_BALCONISTA_CONFIRMADO',
  'INCONCLUSIVO',
  'PROBLEMA_DADOS',
  'PRODUTO_MUDOU_LOCAL',
];
const ETIQUETAS: readonly EtiquetaDoCaso[] = [
  'REPOSICAO_INCOMPLETA',
  'ESTOQUE_INCONSISTENTE',
  'NAO_LOCALIZADO',
  'REINCIDENTE',
  'ATRASADA',
];

/* ------------------------------------------------------------------ *
 * Helpers locais
 * ------------------------------------------------------------------ */

const irParaCaso = (id: string, volta: string) => {
  window.location.hash = enderecoDoCaso(id, volta);
};
/** "−5" com o sinal de menos tipográfico. */
const diferencaTexto = (n: number) => (n < 0 ? `−${String(-n)}` : n > 0 ? `+${String(n)}` : '0');
/** Mais da metade do solicitado faltando pesa mais — só apresentação, não julgamento. */
const tomDaDiferenca = (c: CasoDaFila) =>
  c.diferenca === 0
    ? 'zero'
    : Math.abs(c.diferenca) / Math.max(1, c.solicitado) > 0.5
      ? 'forte'
      : 'media';
const setorBloco = (c: CasoDaFila) =>
  [c.setor, `B${String(c.bloco)}`, c.faixa].filter(Boolean).join(' · ');
const alterna = <T,>(lista: readonly T[], v: T): readonly T[] =>
  lista.includes(v) ? lista.filter((x) => x !== v) : [...lista, v];

const temFiltroAlemDoStatus = (f: Filtros) =>
  Boolean(
    f.setor || f.pessoa || f.busca?.trim() || f.resultados.length || f.etiquetas.length || f.faixa,
  );

/** "01/10/2026 → 03/10/2026" */
function textoDoPeriodo(f: Filtros): string {
  if (f.periodo === 'tudo') return 'Todo o período';
  const { de, ate } = intervalo(f);
  return de === ate ? diaBR(de) : `${diaBR(de)} → ${diaBR(ate)}`;
}

/** "Perfumaria · erro confirmado · hoje" — só o que está de fato aplicado. */
function descreverFiltros(f: Filtros, d: FilaDoAuditor | null): string {
  const partes: string[] = [];
  if (f.setor) partes.push(d?.setores.find((s) => s.id === f.setor)?.nome ?? 'setor escolhido');
  if (f.pessoa) partes.push(d?.pessoas.find((s) => s.id === f.pessoa)?.nome ?? 'pessoa escolhida');
  for (const e of f.estados) partes.push(ROTULO_DO_ESTADO[e].toLowerCase());
  for (const r of f.resultados) partes.push(ROTULO_DO_RESULTADO[r].toLowerCase());
  for (const t of f.etiquetas) partes.push(ROTULO_DA_ETIQUETA[t].toLowerCase());
  if (f.busca?.trim()) partes.push(`“${f.busca.trim()}”`);
  partes.push(textoDoPeriodo(f));
  return partes.filter(Boolean).join(' · ');
}

const plural = (n: number, um: string, varios: string) => `${String(n)} ${n === 1 ? um : varios}`;

function Chevron() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="dv-chev"
    >
      <path d="M5.4 8 10 12.6 14.6 8" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Selos: status e resultado são colunas diferentes
 * ------------------------------------------------------------------ */

function SeloDeStatus({ c, maiusculo }: { c: CasoDaFila; maiusculo?: boolean }) {
  let texto: string = ROTULO_DO_ESTADO[c.estado];
  if (c.estado === 'EM_ANALISE')
    texto = c.analisadaPorMim
      ? 'Em análise · você'
      : c.analisadaPor
        ? `Em análise · ${primeiroNome(c.analisadaPor)}`
        : texto;
  return (
    <span className={`dv-selo dv-selo-${c.estado}`}>{maiusculo ? texto.toUpperCase() : texto}</span>
  );
}
function SeloDeResultado({ c }: { c: CasoDaFila }) {
  if (!c.resultado) return null;
  return (
    <span
      className="dv-selo dv-selo-resultado"
      title={
        c.rebaixado
          ? 'O resultado escolhido foi rebaixado: houve movimento no período que explica a diferença.'
          : undefined
      }
    >
      {ROTULO_DO_RESULTADO[c.resultado]}
      {c.rebaixado ? ' ·' : ''}
      {c.rebaixado && <i className="dv-rebaixado">rebaixado</i>}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * A tela
 * ------------------------------------------------------------------ */

export function CentralDeDivergencias({
  sessao,
  eu,
  aba,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  aba: AbaDaFila;
  parametros: URLSearchParams;
}) {
  const [filtros, setFiltrosBruto] = useState<Filtros>(() => deParametros(parametros));
  // Mexer em qualquer filtro volta para a página 1; só a paginação escolhe outra página.
  const setFiltros = useCallback((v: Filtros | ((f: Filtros) => Filtros)) => {
    setFiltrosBruto((antes) => {
      const novo = typeof v === 'function' ? v(antes) : v;
      return novo.pagina === antes.pagina ? { ...novo, pagina: 1 } : novo;
    });
  }, []);
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [carregando, setCarregando] = useState(false);
  const [folha, setFolha] = useState(false);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  /** "Todas N": o total do servidor para os mesmos filtros SEM status — só quando é conhecido. */
  const [todas, setTodas] = useState<{ chave: string; n: number } | null>(null);
  const pedido = useRef(0);

  const caps = eu?.capacidades ?? [];
  const podeAnalisar = caps.includes('giro.auditar') || caps.includes('auditoria.analisar');
  const gestao = caps.includes('giro.administrar') || caps.includes('auditoria.ver_todos');

  const consulta: FiltrosDaFila = useMemo(() => consultaDe(aba, filtros), [aba, filtros]);
  /** O endereço desta fila com estes filtros: viaja para o caso e é para onde ele volta. */
  const volta = enderecoDaCentral(aba, filtros);
  const voltaRef = useRef(volta);
  voltaRef.current = volta;
  const [personalizar, setPersonalizar] = useState(false);
  const rolagemPendente = useRef<number | null>(retomarRolagem(volta));

  // Filtros no endereço (sem recarregar a página): F5, voltar do caso e link compartilhado
  // reabrem a MESMA fila.
  useEffect(() => {
    if (window.location.hash !== volta) window.history.replaceState(null, '', volta);
  }, [volta]);
  // Saiu para um caso: guarda a rolagem desta fila para voltar exatamente onde estava.
  useEffect(() => {
    const saiu = () => {
      if (window.location.hash.startsWith('#/caso')) guardarRolagem(voltaRef.current);
    };
    window.addEventListener('hashchange', saiu);
    return () => window.removeEventListener('hashchange', saiu);
  }, []);
  const chaveSemStatus = JSON.stringify({ ...consulta, estados: [] });

  const carregar = useCallback(async () => {
    const n = ++pedido.current;
    setCarregando(true);
    const r = await lerFila(consulta);
    if (n !== pedido.current) return;
    setCarregando(false);
    if (r.ok) {
      setEstado({ fase: 'pronto', dados: r.dados });
      const y = rolagemPendente.current;
      if (y !== null) {
        rolagemPendente.current = null;
        requestAnimationFrame(() => window.scrollTo({ top: y }));
      }
      if (!consulta.estados?.length) {
        const n2 = consulta.etiquetas?.length ? r.dados.casos.length : r.dados.total;
        setTodas({ chave: JSON.stringify({ ...consulta, estados: [] }), n: n2 });
      }
    } else {
      setEstado((e) => ({
        fase: 'erro',
        falha: r.falha,
        ultimo: e.fase === 'pronto' ? e.dados : e.fase === 'erro' ? e.ultimo : null,
      }));
    }
  }, [consulta]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Voltar para a tela (outra aba do navegador, outro app no celular) atualiza a fila. Trocar de
  // aba dispara "focus" E "visibilitychange" juntos: uma releitura só (revisão 360).
  useEffect(() => {
    let ultima = 0;
    const voltou = () => {
      if (document.visibilityState !== 'visible' || Date.now() - ultima < 2_000) return;
      ultima = Date.now();
      void carregar();
    };
    window.addEventListener('focus', voltou);
    document.addEventListener('visibilitychange', voltou);
    return () => {
      window.removeEventListener('focus', voltou);
      document.removeEventListener('visibilitychange', voltou);
    };
  }, [carregar]);

  const analisar = useCallback(
    async (c: CasoDaFila) => {
      setAviso(null);
      setAbrindo(c.id);
      const r = await iniciarAnalise(sessao, c.id, c.versao);
      setAbrindo(null);
      if (r.tipo === 'recusada') {
        setAviso(`O caso ${numeroDoCaso(c.numero)} não foi aberto para análise: ${r.mensagem}`);
        void carregar();
        return;
      }
      // Confirmada ou incerta: o caso mostra o estado real ao abrir — incerto não é recusa.
      irParaCaso(c.id, voltaRef.current);
    },
    [sessao, carregar],
  );

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso')
    return <SemAcesso area="A Central de Divergências" />;

  const dados =
    estado.fase === 'pronto' ? estado.dados : estado.fase === 'erro' ? estado.ultimo : null;
  const offline = estado.fase === 'erro' && estado.falha.tipo === 'offline';
  const ind = dados?.indicadores;
  const contagemConfiavel = !temFiltroAlemDoStatus(filtros);
  const totalDaLista = dados ? (filtros.etiquetas.length ? dados.casos.length : dados.total) : 0;
  const nTodas = todas?.chave === chaveSemStatus ? todas.n : null;
  const filtrado =
    temFiltroAlemDoStatus(filtros) || filtros.estados.length > 0 || filtros.periodo !== 'tudo';
  const nFiltros =
    filtros.estados.length +
    filtros.resultados.length +
    filtros.etiquetas.length +
    (filtros.setor ? 1 : 0) +
    (filtros.pessoa ? 1 : 0) +
    (filtros.faixa ? 1 : 0);

  const escolherStatus = (e: EstadoDoCaso | null) =>
    setFiltros((f) => ({ ...f, estados: e ? [e] : [] }));
  const escolherResultado = (r: ResultadoDaAuditoria | null) =>
    setFiltros((f) => ({ ...f, resultados: r ? [r] : [] }));
  const limpar = () =>
    setFiltros((f) => ({
      ...VAZIO,
      periodo: f.periodo,
      de: f.de,
      ate: f.ate,
      abertos: f.abertos,
      etapa: f.etapa,
      anteriores: f.anteriores,
    }));
  const etapa: EtapaDaDivergencia = filtros.etapa ?? ETAPA_INICIAL;
  const escolherEtapa = (e: EtapaDaDivergencia) =>
    setFiltros((f) => ({ ...f, etapa: e, anteriores: false, pagina: 1 }));
  const anteriores = aba === 'fila' && filtros.anteriores;
  const irParaPagina = (n: number) => {
    setFiltros((f) => ({ ...f, pagina: n }));
    window.scrollTo({ top: 0 });
  };
  const escolherPeriodo = (p: Periodo) => {
    setPersonalizar(false);
    setFiltros((f) => ({ ...f, periodo: p, de: null, ate: null }));
  };

  return (
    <section className="dv">
      <nav className="dv-abas" aria-label="Auditoria">
        {ABAS.map((a) => (
          <a
            key={a.aba}
            href={`#/${a.rota}`}
            className={a.aba === aba ? 'dv-aba dv-aba-sel' : 'dv-aba'}
            aria-current={a.aba === aba ? 'page' : undefined}
          >
            {a.rotulo}
            {a.aba === 'recontagens' && ind && ind.recontagens > 0 && (
              <span className="dv-aba-n">{ind.recontagens}</span>
            )}
          </a>
        ))}
        <span className="dv-abas-esp" />
        {dados && <span className="dv-atualizado">atualizado às {hora(dados.atualizadoEm)}</span>}
      </nav>

      <div className="dv-corpo">
        {offline && dados && (
          <div className="dv-offline" role="status">
            <Icone nome="db" tamanho={16} traco={1.6} />
            <span>Sem conexão. Últimos casos carregados às {hora(dados.atualizadoEm)}.</span>
            <button type="button" onClick={() => void carregar()}>
              Tentar agora
            </button>
          </div>
        )}
        {aviso && (
          <div className="dv-aviso" role="alert">
            <Icone nome="warn" tamanho={15} traco={1.7} />
            <span>{aviso}</span>
            <button type="button" aria-label="Fechar aviso" onClick={() => setAviso(null)}>
              <Icone nome="x" tamanho={14} traco={1.8} />
            </button>
          </div>
        )}

        {/* Cabeçalho do celular (artboard B) */}
        <div className="dv-cabeca-movel">
          <div>
            <h1>
              {aba === 'fila'
                ? `Divergências · ${anteriores ? 'Anteriores' : ROTULO_DA_ETAPA[etapa]}`
                : TITULO_DA_ABA[aba]}
            </h1>
            <span>
              Olá, {eu ? primeiroNome(eu.nomeExibicao || eu.nome) : ''}
              {ind ? ` · ${plural(ind.novas, 'nova', 'novas')}` : ''}
              {dados ? ` · atualizado às ${hora(dados.atualizadoEm)}` : ''}
            </span>
          </div>
          <button
            type="button"
            className="dv-botao-filtro-movel"
            aria-label="Filtros"
            onClick={() => setFolha(true)}
          >
            <Icone nome="filter" tamanho={18} traco={1.6} />
            {nFiltros > 0 && <span className="dv-bolinha">{nFiltros}</span>}
          </button>
        </div>

        {/* Cabeçalho do desktop (artboard A) */}
        <div className="dv-topo">
          <div className="dv-titulo">
            <h1>{TITULO_DA_ABA[aba]}</h1>
            <span
              title={
                aba === 'fila' && dados
                  ? `Prioridade: ${PRIORIDADE[dados.prioridadePor]}`
                  : undefined
              }
            >
              {aba === 'fila'
                ? `${SUBTITULO_DA_ETAPA[etapa]} · Divergências que nasceram no período escolhido${filtros.abertos ? ' + abertas de antes' : ''} · ${ORDENS.find((o) => o.ordem === filtros.ordem)?.rotulo ?? ''}${filtros.ordem === 'PRIORIDADE' && dados ? ` (${PRIORIDADE[dados.prioridadePor]})` : ''}`
                : aba === 'recontagens'
                  ? 'Casos que pedem recontagem física na loja antes de concluir'
                  : aba === 'concluidas'
                    ? 'Concluídos no período escolhido, com o resultado vigente de cada caso'
                    : 'Divergências que nasceram no período escolhido, em qualquer status'}
            </span>
          </div>
          <div className="dv-topo-dir">
            {aba !== 'recontagens' && (
              <div className="dv-segmento" role="group" aria-label="Período">
                {PERIODOS.map((p) => (
                  <button
                    key={p.periodo}
                    type="button"
                    aria-pressed={filtros.periodo === p.periodo}
                    className={filtros.periodo === p.periodo ? 'dv-seg-sel' : ''}
                    onClick={() => escolherPeriodo(p.periodo)}
                  >
                    {p.rotulo}
                  </button>
                ))}
                <button
                  type="button"
                  aria-pressed={filtros.periodo === 'periodo'}
                  aria-expanded={personalizar}
                  className={filtros.periodo === 'periodo' ? 'dv-seg-sel' : ''}
                  onClick={() => setPersonalizar((v) => !v)}
                >
                  📅 Período
                </button>
              </div>
            )}
            <span className="dv-mais-ancora">
              <button
                type="button"
                className="dv-botao"
                aria-expanded={folha}
                onClick={() => setFolha((v) => !v)}
              >
                <Icone nome="filter" tamanho={14} traco={1.6} />
                Mais filtros
                {nFiltros > 0 && <span className="dv-bolinha">{nFiltros}</span>}
              </button>
            </span>
          </div>
        </div>

        {aba !== 'recontagens' && (
          <PeriodoAplicado
            filtros={filtros}
            total={dados ? totalDaLista : null}
            aberto={personalizar}
            aoAplicar={(de, ate) => {
              setPersonalizar(false);
              setFiltros((f) => ({ ...f, periodo: 'periodo', de, ate }));
            }}
            aoFechar={() => setPersonalizar(false)}
            aoAbertos={(v) => setFiltros((f) => ({ ...f, abertos: v }))}
          />
        )}

        {aba === 'fila' && (
          <div className="dv-etapas" role="tablist" aria-label="Etapa da divergência">
            {ETAPAS_DA_FILA.map((e) => {
              const n =
                e === 'PRATELEIRA'
                  ? ind?.aguardandoPrateleira
                  : e === 'ESTOQUE'
                    ? ind?.aguardandoEstoque
                    : ind?.aguardandoAjuste;
              return (
                <button
                  key={e}
                  type="button"
                  role="tab"
                  aria-selected={!anteriores && etapa === e}
                  className={!anteriores && etapa === e ? 'dv-etapa dv-etapa-sel' : 'dv-etapa'}
                  onClick={() => escolherEtapa(e)}
                >
                  <span className="dv-etapa-rotulo">{ROTULO_DA_ETAPA[e]}</span>
                  <span className={n ? 'dv-etapa-n dv-etapa-n-cheio' : 'dv-etapa-n'}>
                    {n ?? '—'}
                  </span>
                  <span className="dv-etapa-sub">{SUBTITULO_DA_ETAPA[e]}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="dv-filtros">
          {aba === 'historico' && (
            <>
              <button
                type="button"
                className={`dv-chip dv-chip-todas ${filtros.estados.length === 0 ? 'dv-chip-sel' : ''}`}
                aria-pressed={filtros.estados.length === 0}
                onClick={() => escolherStatus(null)}
              >
                Todas{nTodas !== null ? ` ${String(nTodas)}` : ''}
              </button>
              {CHIPS_DE_STATUS.filter(
                (s) => s.estado !== 'REABERTA' || (ind?.reabertas ?? 0) > 0,
              ).map((s) => {
                const sel = filtros.estados.length === 1 && filtros.estados[0] === s.estado;
                const n = ind && contagemConfiavel ? s.conta(ind) : null;
                return (
                  <button
                    key={s.estado}
                    type="button"
                    aria-pressed={sel}
                    className={`dv-chip ${s.tom && n ? `dv-chip-${s.tom}` : ''} ${sel ? 'dv-chip-sel' : ''}`}
                    onClick={() => escolherStatus(sel ? null : s.estado)}
                  >
                    <span className="dv-so-desk">{s.rotulo}</span>
                    <span className="dv-so-movel">{s.curto}</span>
                    {n !== null ? ` ${String(n)}` : ''}
                  </button>
                );
              })}
            </>
          )}
          {aba === 'concluidas' && (
            <>
              <button
                type="button"
                className={`dv-chip ${filtros.resultados.length === 0 ? 'dv-chip-sel' : ''}`}
                aria-pressed={filtros.resultados.length === 0}
                onClick={() => escolherResultado(null)}
              >
                Todos os resultados
              </button>
              {RESULTADOS.map((r) => {
                const sel = filtros.resultados.length === 1 && filtros.resultados[0] === r;
                return (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={sel}
                    className={`dv-chip ${sel ? 'dv-chip-sel' : ''}`}
                    onClick={() => escolherResultado(sel ? null : r)}
                  >
                    {ROTULO_DO_RESULTADO[r]}
                  </button>
                );
              })}
            </>
          )}
          {aba !== 'recontagens' && <span className="dv-divisor" aria-hidden="true" />}
          <label className="dv-select">
            <span>Categoria</span>
            <select
              value={filtros.setor ?? ''}
              onChange={(e) => setFiltros((f) => ({ ...f, setor: e.target.value || null }))}
            >
              <option value="">Todos</option>
              {dados?.setores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
            <Chevron />
          </label>
          <label className="dv-select">
            <span>Balconista</span>
            <select
              value={filtros.pessoa ?? ''}
              onChange={(e) => setFiltros((f) => ({ ...f, pessoa: e.target.value || null }))}
            >
              <option value="">Todos</option>
              {dados?.pessoas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
            <Chevron />
          </label>
          <label className="dv-select">
            <span>Diferença</span>
            <select
              value={filtros.faixa}
              onChange={(e) =>
                setFiltros((f) => ({ ...f, faixa: e.target.value as FaixaDaDiferenca }))
              }
            >
              {FAIXAS.map((x) => (
                <option key={x.faixa} value={x.faixa}>
                  {x.rotulo}
                </option>
              ))}
            </select>
            <Chevron />
          </label>
          <label className="dv-select">
            <span>Ordenar</span>
            <select
              value={filtros.ordem}
              onChange={(e) => setFiltros((f) => ({ ...f, ordem: e.target.value as OrdemDaFila }))}
            >
              {ORDENS.map((o) => (
                <option key={o.ordem} value={o.ordem}>
                  {o.rotulo}
                </option>
              ))}
            </select>
            <Chevron />
          </label>
          {filtros.busca?.trim() && (
            <button
              type="button"
              className="dv-chip dv-chip-busca"
              onClick={() => setFiltros((f) => ({ ...f, busca: null }))}
              aria-label={`Tirar a busca ${filtros.busca}`}
            >
              “{filtros.busca.trim()}”
              <Icone nome="x" tamanho={12} traco={1.8} />
            </button>
          )}
          <span className="dv-esp" />
          {aba !== 'recontagens' && aba !== 'fila' && (
            <span className="dv-nota-filtro">
              status particiona a fila · <b>tags podem se sobrepor</b>
            </span>
          )}
        </div>

        {folha && (
          <FolhaDeFiltros
            filtros={filtros}
            dados={dados}
            aoAplicar={(f) => {
              setFiltros(f);
              setFolha(false);
            }}
            aoFechar={() => setFolha(false)}
          />
        )}

        {estado.fase === 'erro' && !dados ? (
          estado.falha.tipo === 'offline' ? (
            <div className="dv-cartao-estado">
              <EstadoVazio
                icone="wifi"
                titulo="Sem conexão"
                texto="Não foi possível carregar a fila de divergências. Nenhum caso foi carregado ainda neste aparelho."
              >
                <button type="button" className="es-botao" onClick={() => void carregar()}>
                  <Icone nome="cycle" tamanho={15} traco={1.6} />
                  Tentar novamente
                </button>
              </EstadoVazio>
            </div>
          ) : (
            <ErroDeArea oQue="a fila de divergências" aoTentar={() => void carregar()} />
          )
        ) : !dados ? (
          <Esqueleto />
        ) : dados.casos.length === 0 ? (
          filtrado ? (
            <div className="dv-cartao-estado dv-sem-resultado">
              <div className="dv-sr-titulo">Nenhum caso com estes filtros</div>
              <div className="dv-sr-texto">{descreverFiltros(filtros, dados)}</div>
              <button type="button" className="dv-botao dv-botao-alto" onClick={limpar}>
                Limpar filtros
              </button>
            </div>
          ) : aba === 'fila' && !anteriores ? (
            <FilaVazia etapa={etapa} />
          ) : (
            <div className="dv-cartao-estado">
              <EstadoVazio
                icone={aba === 'recontagens' ? 'scan' : 'check'}
                titulo={
                  aba === 'recontagens'
                    ? 'Nenhuma recontagem pendente'
                    : aba === 'concluidas'
                      ? 'Nenhum caso concluído hoje'
                      : 'Nenhuma divergência aberta hoje'
                }
                texto="Troque o período para ver mais dias."
              />
            </div>
          )
        ) : (
          <div className={carregando ? 'dv-lista dv-recarregando' : 'dv-lista'}>
            {aba === 'fila' && !anteriores ? (
              <>
                <TabelaDeEtapa etapa={etapa} dados={dados} volta={volta} />
                <CartoesDeEtapa etapa={etapa} dados={dados} volta={volta} />
                <Paginacao dados={dados} aoIr={irParaPagina} />
              </>
            ) : (
              <>
                <Tabela
                  casos={dados.casos}
                  total={totalDaLista}
                  podeAnalisar={podeAnalisar}
                  abrindo={abrindo}
                  aoAnalisar={(c) => void analisar(c)}
                  volta={volta}
                />
                <Cartoes
                  casos={dados.casos}
                  total={totalDaLista}
                  podeAnalisar={podeAnalisar}
                  abrindo={abrindo}
                  aoAnalisar={(c) => void analisar(c)}
                  volta={volta}
                />
                {anteriores && <Paginacao dados={dados} aoIr={irParaPagina} />}
              </>
            )}
          </div>
        )}

        {gestao && dados && (
          <RelatorioEAnalise consulta={consulta} filtros={filtros} dados={dados} />
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Peças
 * ------------------------------------------------------------------ */

function Esqueleto() {
  return (
    <div className="dv-esqueleto" aria-busy="true" aria-label="Carregando a fila">
      <div className="dv-esq-topo" />
      {[0, 1, 2, 3].map((k) => (
        <div key={k} className="dv-esq-linha">
          <i style={{ width: '14%' }} />
          <i style={{ width: '30%' }} />
          <span />
          <i style={{ width: '12%' }} />
        </div>
      ))}
    </div>
  );
}

const VAZIA_DA_ETAPA: Readonly<Record<EtapaDaDivergencia, string>> = {
  PRATELEIRA: 'Nenhuma prateleira aguardando conferência',
  ESTOQUE: 'Nenhuma busca no estoque aguardando',
  AJUSTE: 'Nenhum caso aguardando decisão',
  CONCLUIDA: 'Nenhum caso concluído',
};

function FilaVazia({ etapa }: { etapa: EtapaDaDivergencia }) {
  return (
    <div className="dv-cartao-estado">
      <div className="dv-fila-vazia">
        <span className="dv-fv-icone" aria-hidden="true">
          <Icone nome="check" tamanho={20} traco={2} />
        </span>
        <div className="dv-fv-titulo">{VAZIA_DA_ETAPA[etapa]}</div>
        <div className="dv-fv-texto">Quando um caso chegar a esta etapa ele aparece aqui.</div>
      </div>
    </div>
  );
}

type PropsDaLista = {
  casos: readonly CasoDaFila[];
  total: number;
  podeAnalisar: boolean;
  abrindo: string | null;
  aoAnalisar: (c: CasoDaFila) => void;
  /** Endereço desta fila: viaja para o caso. */
  volta: string;
};

/** A ação da linha. "Analisar" começa a análise; o resto só abre o caso. */
function acaoDe(c: CasoDaFila, podeAnalisar: boolean) {
  if ((c.estado === 'NOVA' || c.estado === 'REABERTA') && podeAnalisar)
    return { tipo: 'analisar' as const, rotulo: 'Analisar' };
  if (c.estado === 'EM_ANALISE' && c.analisadaPorMim)
    return { tipo: 'abrir' as const, rotulo: 'Continuar' };
  if (c.estado === 'RECONTAGEM' && podeAnalisar)
    return { tipo: 'abrir' as const, rotulo: 'Recontar' };
  if (c.estado === 'AGUARDANDO_DADOS' && c.evidencias === 0)
    return { tipo: 'ver' as const, rotulo: 'sem evidência' };
  return { tipo: 'ver' as const, rotulo: 'Ver' };
}

function Evidencias({ c }: { c: CasoDaFila }) {
  if (c.evidencias > 0)
    return (
      <span className="dv-evid" title={`${plural(c.evidencias, 'foto', 'fotos')} de evidência`}>
        <Icone nome="cam" tamanho={12} traco={1.6} />
        {c.evidencias}
      </span>
    );
  if (c.recontagens > 0)
    return (
      <span className="dv-evid" title={plural(c.recontagens, 'recontagem', 'recontagens')}>
        <Icone nome="scan" tamanho={12} traco={1.6} />
        recont.
      </span>
    );
  return <span className="dv-traco">—</span>;
}

function Etiquetas({ c }: { c: CasoDaFila }) {
  const visiveis = c.etiquetas.filter((e) => e === 'ATRASADA' || e === 'REINCIDENTE');
  if (!visiveis.length) return null;
  return (
    <>
      {visiveis.map((e) => (
        <span key={e} className={`dv-tag dv-tag-${e}`}>
          {e === 'REINCIDENTE' && c.reincidencias > 1
            ? `${ROTULO_DA_ETIQUETA[e]} ${String(c.reincidencias)}×`
            : ROTULO_DA_ETIQUETA[e]}
        </span>
      ))}
    </>
  );
}

function Tabela({ casos, total, podeAnalisar, abrindo, aoAnalisar, volta }: PropsDaLista) {
  return (
    <div className="dv-tabela-cartao">
      <div className="dv-rolagem">
        <table className="dv-tabela">
          <thead>
            <tr>
              <th>Caso</th>
              <th>Produto</th>
              <th>Categoria · bloco</th>
              <th>Autoria do item</th>
              <th className="dv-dir">Solic.</th>
              <th className="dv-dir">Encontr.</th>
              <th className="dv-dir">Difer.</th>
              <th>Evid.</th>
              <th className="dv-dir">Aberto há</th>
              <th>Status</th>
              <th className="dv-dir">
                <span className="dv-sr">Ação</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {casos.map((c, k) => {
              const a = acaoDe(c, podeAnalisar);
              return (
                <tr
                  key={c.id}
                  className={k === 0 && a.tipo === 'analisar' ? 'dv-linha-primeira' : ''}
                >
                  <td className="dv-num">{numeroDoCaso(c.numero)}</td>
                  <td>
                    <div className="dv-produto-com-foto">
                      <FotoDoProduto url={c.fotoProduto} nome={c.produto} tamanho={40} />
                      <div>
                        <div className="dv-produto">{c.produto}</div>
                        <div className="dv-codigo">
                          {c.fabricante ?? 'Laboratório não informado'} · {c.codigo ?? 'sem código'}
                          <Etiquetas c={c} />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="dv-cinza">{setorBloco(c)}</td>
                  <td className={c.autoria ? '' : 'dv-dados'}>{c.autoria ?? 'não identificada'}</td>
                  <td className="dv-dir">{c.solicitado}</td>
                  <td className="dv-dir">{c.encontrado}</td>
                  <td className={`dv-dir dv-dif dv-dif-${tomDaDiferenca(c)}`}>
                    {diferencaTexto(c.diferenca)}
                  </td>
                  <td>
                    <Evidencias c={c} />
                  </td>
                  <td
                    className={`dv-dir ${c.atrasada ? 'dv-atrasada' : 'dv-cinza'}`}
                    title={c.atrasada ? 'Prazo de análise vencido' : undefined}
                  >
                    {ha(c.abertoEm)}
                  </td>
                  <td>
                    <div className="dv-selos">
                      <SeloDeStatus c={c} />
                      <SeloDeResultado c={c} />
                    </div>
                  </td>
                  <td className="dv-dir">
                    {a.tipo === 'analisar' ? (
                      <button
                        type="button"
                        className="dv-acao"
                        disabled={abrindo !== null}
                        onClick={() => aoAnalisar(c)}
                      >
                        {abrindo === c.id ? 'Abrindo…' : a.rotulo}
                      </button>
                    ) : (
                      <a
                        href={enderecoDoCaso(c.id, volta)}
                        className={a.tipo === 'abrir' ? 'dv-acao' : 'dv-acao dv-acao-ver'}
                        aria-label={`${a.rotulo} — caso ${numeroDoCaso(c.numero)}`}
                      >
                        {a.rotulo}
                      </a>
                    )}
                    {podeAnalisar && c.estado !== 'CONCLUIDA' && (
                      <a
                        href={`${enderecoDoCaso(c.id, volta)}&modo=recontar`}
                        className="dv-acao dv-acao-ver dv-acao-recontar"
                        aria-label={`Recontar — caso ${numeroDoCaso(c.numero)}`}
                      >
                        Recontar
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="dv-rodape">
        <span>
          1–{casos.length} de {plural(total, 'caso', 'casos')}
        </span>
        <span className="dv-rodape-nota">
          Status e resultado são colunas diferentes · autoria é do item, não do bloco · um caso pode
          ter várias tags e continua sendo 1 caso
        </span>
      </div>
    </div>
  );
}

function Cartoes({ casos, total, podeAnalisar, abrindo, aoAnalisar, volta }: PropsDaLista) {
  // O primeiro caso pronto para análise ganha o botão grande — como no artboard B.
  const primeiro = casos.find((c) => acaoDe(c, podeAnalisar).tipo === 'analisar')?.id;
  return (
    <div className="dv-cartoes">
      {casos.map((c) => {
        const a = acaoDe(c, podeAnalisar);
        const destaque = c.id === primeiro;
        const recontar = c.estado === 'RECONTAGEM' && podeAnalisar;
        const aguardando = c.estado === 'AGUARDANDO_DADOS';
        const corpo = (
          <>
            <div className="dv-c-topo">
              <span className="dv-num">{numeroDoCaso(c.numero)}</span>
              <SeloDeStatus c={c} maiusculo />
              <SeloDeResultado c={c} />
              <span className="dv-esp" />
              <span className={c.atrasada ? 'dv-atrasada' : 'dv-c-ha'}>{ha(c.abertoEm)}</span>
            </div>
            <div className="dv-produto-com-foto">
              <FotoDoProduto url={c.fotoProduto} nome={c.produto} tamanho={44} />
              <div>
                <div className="dv-c-produto">{c.produto}</div>
                <div className="dv-codigo">{c.fabricante ?? 'Laboratório não informado'}</div>
                <div className="dv-codigo">
                  <span>Cód. {c.codigo ?? '—'}</span>
                  <span>EAN {c.ean ?? '—'}</span>
                </div>
              </div>
            </div>
            <div className="dv-c-local">{setorBloco(c)}</div>
            {aguardando ? (
              <div className="dv-c-dados">
                {[
                  c.autoria ? null : 'Autoria do item não identificada.',
                  'Aguardando dados do sistema.',
                  c.evidencias === 0 ? 'Sem evidência suficiente para analisar ainda.' : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </div>
            ) : (
              <>
                {destaque && c.autoria && (
                  <div className="dv-c-autoria">Item conferido por {c.autoria}</div>
                )}
                {!recontar && (
                  <div className="dv-c-numeros">
                    <div>
                      <span>{destaque ? 'PRATELEIRA' : 'PRATEL.'}</span>
                      <b>{c.prateleira ?? '—'}</b>
                    </div>
                    <div>
                      <span>ESTOQUE</span>
                      <b>{c.estoqueAtual ?? '—'}</b>
                    </div>
                    <div>
                      <span>{destaque ? 'SOLICITADO' : 'SOLIC.'}</span>
                      <b>{c.solicitado}</b>
                    </div>
                    <div>
                      <span>{destaque ? 'ENCONTRADO' : 'ENCONTR.'}</span>
                      <b>{c.encontrado}</b>
                    </div>
                    <div>
                      <span>{destaque ? 'DIFERENÇA' : 'DIFER.'}</span>
                      <b className={`dv-dif-${tomDaDiferenca(c)}`}>{diferencaTexto(c.diferenca)}</b>
                    </div>
                    <span className="dv-esp" />
                    {destaque ? (
                      <Evidencias c={c} />
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="dv-chev"
                      >
                        <path d="M8 5.4 12.6 10 8 14.6" />
                      </svg>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        );
        const classe = `dv-cartao ${destaque ? 'dv-cartao-destaque' : ''} ${aguardando ? 'dv-cartao-dados' : ''}`;
        if (destaque)
          return (
            <div key={c.id} className={classe}>
              {corpo}
              <button
                type="button"
                className="dv-c-botao"
                disabled={abrindo !== null}
                onClick={() => aoAnalisar(c)}
              >
                {abrindo === c.id ? 'Abrindo…' : 'Analisar'}
              </button>
            </div>
          );
        if (recontar)
          return (
            <div key={c.id} className={classe}>
              {corpo}
              <a className="dv-c-botao dv-c-botao-contorno" href={enderecoDoCaso(c.id, volta)}>
                <Icone nome="scan" tamanho={18} traco={1.6} />
                Fazer recontagem
              </a>
            </div>
          );
        return (
          <a
            key={c.id}
            className={`${classe} dv-cartao-link`}
            href={enderecoDoCaso(c.id, volta)}
            aria-label={`${a.rotulo === 'sem evidência' ? 'Ver' : a.rotulo} — caso ${numeroDoCaso(c.numero)}, ${c.produto}`}
          >
            {corpo}
          </a>
        );
      })}
      <div className="dv-c-rodape">
        1–{casos.length} de {plural(total, 'caso', 'casos')}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Bottom sheet (celular) / painel (desktop) — "Mais filtros"
 * ------------------------------------------------------------------ */

function FolhaDeFiltros({
  filtros,
  dados,
  aoAplicar,
  aoFechar,
}: {
  filtros: Filtros;
  dados: FilaDoAuditor | null;
  aoAplicar: (f: Filtros) => void;
  aoFechar: () => void;
}) {
  const [f, setF] = useState<Filtros>(filtros);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);
  const chip = (sel: boolean, rotulo: string, aoClicar: () => void, chave: string) => (
    <button
      key={chave}
      type="button"
      aria-pressed={sel}
      className={sel ? 'dv-fchip dv-fchip-sel' : 'dv-fchip'}
      onClick={aoClicar}
    >
      {rotulo}
    </button>
  );
  return (
    <>
      <button type="button" className="dv-veu" aria-label="Fechar filtros" onClick={aoFechar} />
      <div className="dv-folha" role="dialog" aria-modal="true" aria-labelledby="dv-folha-titulo">
        <div className="dv-folha-alca" aria-hidden="true" />
        <div className="dv-folha-topo">
          <h2 id="dv-folha-titulo">Filtros</h2>
          <button
            type="button"
            className="dv-limpar"
            onClick={() =>
              setF({
                ...VAZIO,
                periodo: f.periodo,
                de: f.de,
                ate: f.ate,
                abertos: f.abertos,
                etapa: f.etapa,
              })
            }
          >
            Limpar
          </button>
        </div>
        <div className="dv-folha-corpo">
          <div className="dv-folha-grupo dv-so-movel-bloco">
            <div className="dv-folha-rotulo">PERÍODO (DIA DA DIVERGÊNCIA)</div>
            <div className="dv-fchips">
              {PERIODOS.map((p) =>
                chip(
                  f.periodo === p.periodo,
                  p.rotulo,
                  () => setF({ ...f, periodo: p.periodo, de: null, ate: null }),
                  p.rotulo,
                ),
              )}
            </div>
            <div className="dv-periodo-form">
              <label>
                De
                <input
                  type="date"
                  value={intervalo(f).de}
                  onChange={(e) => setF({ ...f, periodo: 'periodo', de: e.target.value || null })}
                />
              </label>
              <label>
                Até
                <input
                  type="date"
                  value={intervalo(f).ate}
                  onChange={(e) => setF({ ...f, periodo: 'periodo', ate: e.target.value || null })}
                />
              </label>
            </div>
            <label className="dv-periodo-abertos">
              <input
                type="checkbox"
                checked={f.abertos}
                onChange={(e) => setF({ ...f, abertos: e.target.checked })}
              />
              Incluir casos antigos ainda abertos
            </label>
          </div>
          <div className="dv-folha-grupo">
            <div className="dv-folha-rotulo">DIFERENÇA</div>
            <div className="dv-fchips">
              {FAIXAS.map((x) =>
                chip(
                  f.faixa === x.faixa,
                  x.rotulo,
                  () => setF({ ...f, faixa: x.faixa }),
                  `dif${x.faixa}`,
                ),
              )}
            </div>
          </div>
          <div className="dv-folha-grupo">
            <div className="dv-folha-rotulo">ORDENAR</div>
            <div className="dv-fchips">
              {ORDENS.map((o) =>
                chip(f.ordem === o.ordem, o.rotulo, () => setF({ ...f, ordem: o.ordem }), o.ordem),
              )}
            </div>
          </div>
          <div className="dv-folha-grupo">
            <div className="dv-folha-rotulo">STATUS</div>
            <div className="dv-fchips">
              {ESTADOS_DO_FILTRO.map((s) =>
                chip(
                  f.estados.includes(s.estado),
                  s.rotulo,
                  () => setF({ ...f, estados: alterna(f.estados, s.estado) }),
                  s.estado,
                ),
              )}
            </div>
          </div>
          <div className="dv-folha-grupo">
            <div className="dv-folha-rotulo">RESULTADO</div>
            <div className="dv-fchips">
              {RESULTADOS.map((r) =>
                chip(
                  f.resultados.includes(r),
                  ROTULO_DO_RESULTADO[r],
                  () => setF({ ...f, resultados: alterna(f.resultados, r) }),
                  r,
                ),
              )}
            </div>
          </div>
          <div className="dv-folha-grupo">
            <div className="dv-folha-rotulo">TAGS DO CASO</div>
            <div className="dv-fchips">
              {ETIQUETAS.map((t) =>
                chip(
                  f.etiquetas.includes(t),
                  ROTULO_DA_ETIQUETA[t],
                  () => setF({ ...f, etiquetas: alterna(f.etiquetas, t) }),
                  t,
                ),
              )}
            </div>
          </div>
          <div className="dv-folha-selects">
            <label className="dv-fselect">
              <span>Categoria</span>
              <select
                value={f.setor ?? ''}
                onChange={(e) => setF({ ...f, setor: e.target.value || null })}
              >
                <option value="">Todos</option>
                {dados?.setores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="dv-fselect">
              <span>Pessoa</span>
              <select
                value={f.pessoa ?? ''}
                onChange={(e) => setF({ ...f, pessoa: e.target.value || null })}
              >
                <option value="">Todas</option>
                {dados?.pessoas.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <button type="button" className="dv-aplicar" onClick={() => aoAplicar(f)}>
          Aplicar filtros
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * C — Relatório de auditoria e análise de divergências (gestão)
 * ------------------------------------------------------------------ */

function RelatorioEAnalise({
  consulta,
  filtros,
  dados,
}: {
  consulta: FiltrosDaFila;
  filtros: Filtros;
  dados: FilaDoAuditor;
}) {
  const aplicados: string[] = [textoDoPeriodo(filtros)];
  aplicados.push(
    filtros.resultados.length
      ? filtros.resultados.map((r) => ROTULO_DO_RESULTADO[r]).join(', ')
      : 'Todos os resultados',
  );
  if (filtros.estados.length)
    aplicados.push(filtros.estados.map((e) => ROTULO_DO_ESTADO[e]).join(', '));
  aplicados.push(
    filtros.setor
      ? (dados.setores.find((s) => s.id === filtros.setor)?.nome ?? 'Setor escolhido')
      : 'Todos os setores',
  );
  aplicados.push(
    filtros.pessoa
      ? (dados.pessoas.find((s) => s.id === filtros.pessoa)?.nome ?? 'Pessoa escolhida')
      : 'Todas as pessoas',
  );
  for (const t of filtros.etiquetas) aplicados.push(ROTULO_DA_ETIQUETA[t]);
  if (filtros.busca?.trim()) aplicados.push(`“${filtros.busca.trim()}”`);

  // Incidência ≠ conclusão: conta por produto sobre os casos CARREGADOS nesta tela.
  const porProduto = useMemo(() => {
    const m = new Map<
      string,
      {
        produto: string;
        codigo: string | null;
        setor: string;
        n: number;
        erro: number;
        dados: number;
        local: number;
      }
    >();
    for (const c of dados.casos) {
      const l = m.get(c.produtoId) ?? {
        produto: c.produto,
        codigo: c.codigo,
        setor: c.setor,
        n: 0,
        erro: 0,
        dados: 0,
        local: 0,
      };
      l.n += 1;
      if (c.resultado === 'ERRO_BALCONISTA_CONFIRMADO') l.erro += 1;
      if (c.resultado === 'PROBLEMA_DADOS') l.dados += 1;
      if (c.resultado === 'PRODUTO_MUDOU_LOCAL') l.local += 1;
      m.set(c.produtoId, l);
    }
    return [...m.values()].sort((a, b) => b.n - a.n || a.produto.localeCompare(b.produto));
  }, [dados.casos]);
  const linhas = porProduto.slice(0, 8);
  const maior = (k: 'erro' | 'dados' | 'local') => Math.max(0, ...linhas.map((l) => l[k]));

  return (
    <div className="dv-gestao">
      <div className="dv-gcartao dv-relatorio">
        <div className="dv-gcabeca">
          <h2>Relatório de auditoria</h2>
          <span>Herda período, filtros e ordenação da fila</span>
        </div>
        <div className="dv-gcorpo">
          <div className="dv-folha-rotulo">FILTROS APLICADOS</div>
          <div className="dv-aplicados">
            {aplicados.filter(Boolean).map((t, k) => (
              <span key={t} className={k === 0 ? 'dv-apl dv-apl-sel' : 'dv-apl'}>
                {t}
              </span>
            ))}
          </div>
          <div className="dv-folha-rotulo">
            FORMATO <span className="dv-rotulo-leve">· planilha CSV (abre no Excel)</span>
          </div>
          <a className="dv-gerar" href={urlDoRelatorio(consulta)} download="auditoria-giro.csv">
            <Icone nome="down" tamanho={16} traco={1.7} />
            Gerar relatório
          </a>
        </div>
      </div>

      {linhas.length > 0 && (
        <div className="dv-gcartao dv-analise">
          <div className="dv-gcabeca dv-gcabeca-linha">
            <div>
              <h2>Análise de divergências</h2>
              <span>
                {plural(dados.casos.length, 'caso carregado', 'casos carregados')} nesta tela ·
                incidência e conclusão em colunas distintas
              </span>
            </div>
            <span className="dv-gnota">mais divergência ≠ mais erro confirmado</span>
          </div>
          <div className="dv-rolagem">
            <table className="dv-tabela dv-tabela-analise">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th className="dv-dir">Diverg.</th>
                  <th className="dv-dir">Erro conf.</th>
                  <th className="dv-dir">Probl. dados</th>
                  <th className="dv-dir">Mudou local</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={`${l.produto}-${l.codigo ?? ''}`}>
                    <td>
                      <div className="dv-produto">{l.produto}</div>
                      <div className="dv-codigo">
                        {[l.codigo, l.setor].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="dv-dir dv-forte">{l.n}</td>
                    <td
                      className={`dv-dir ${l.erro > 0 && l.erro === maior('erro') ? 'dv-forte dv-dif-forte' : ''}`}
                    >
                      {l.erro}
                    </td>
                    <td
                      className={`dv-dir ${l.dados > 0 && l.dados === maior('dados') ? 'dv-forte' : ''}`}
                    >
                      {l.dados}
                    </td>
                    <td
                      className={`dv-dir ${l.local > 0 && l.local === maior('local') ? 'dv-forte' : ''}`}
                    >
                      {l.local}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dv-gpe">
            Conta feita sobre os casos carregados acima
            {porProduto.length > linhas.length
              ? ` (os ${String(linhas.length)} produtos com mais casos, de ${String(porProduto.length)})`
              : ''}
            . Setores e horários com mais divergência costumam apontar processo, disposição física
            ou dado — não pessoas.
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Período aplicado — sempre visível; personalizado com De → Até
 * ------------------------------------------------------------------ */

function PeriodoAplicado({
  filtros,
  total,
  aberto,
  aoAplicar,
  aoFechar,
  aoAbertos,
}: {
  filtros: Filtros;
  total: number | null;
  aberto: boolean;
  aoAplicar: (de: string, ate: string) => void;
  aoFechar: () => void;
  aoAbertos: (v: boolean) => void;
}) {
  const atual = intervalo(filtros);
  const [de, setDe] = useState(atual.de);
  const [ate, setAte] = useState(atual.ate);
  useEffect(() => {
    if (!aberto) return;
    setDe(atual.de);
    setAte(atual.ate);
  }, [aberto]);
  const limite = hojeBR();
  const valido = Boolean(de && ate && de <= ate && ate <= limite);
  return (
    <div className="dv-periodo">
      <div className="dv-periodo-linha">
        <span className="dv-periodo-texto">
          📅 <b>{textoDoPeriodo(filtros)}</b>
          {total !== null && ` · ${plural(total, 'divergência', 'divergências')}`}
        </span>
        <label className="dv-periodo-abertos">
          <input
            type="checkbox"
            checked={filtros.abertos}
            onChange={(e) => aoAbertos(e.target.checked)}
          />
          Incluir casos antigos ainda abertos
        </label>
      </div>
      {aberto && (
        <form
          className="dv-periodo-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido) aoAplicar(de, ate);
          }}
        >
          <label>
            Data inicial
            <input type="date" value={de} max={limite} onChange={(e) => setDe(e.target.value)} />
          </label>
          <label>
            Data final
            <input type="date" value={ate} max={limite} onChange={(e) => setAte(e.target.value)} />
          </label>
          <button type="submit" className="dv-botao dv-botao-alto" disabled={!valido}>
            Aplicar período
          </button>
          <button type="button" className="dv-botao" onClick={aoFechar}>
            Cancelar
          </button>
          {de > ate && <span className="dv-periodo-erro">A data final vem antes da inicial.</span>}
        </form>
      )}
    </div>
  );
}
